import React, { useState, useEffect } from 'react';
import { ButtonGroup, Button, Form, Dropdown } from 'react-bootstrap';
import TreeMapModal from './TreeMapModal';
import './TreeMapContainer.css';

function TreeMapContainer({
  show,
  onHide,
  tickers,
  cash,
  previousBalance,
  preBalLoading = false,
  lastRefreshTime,
  refreshInterval,
  autoRefreshEnabled,
  onRefresh,
  marketInfo,
  inline = false,
  pageKey = ''
}) {
  // Load dual mode preference from localStorage
  const [isDualMode, setIsDualMode] = useState(() => {
    const stored = localStorage.getItem(`treemapDualMode-${pageKey}`);
    return stored === 'true';
  });

  // Load layout preference from localStorage
  const [layout, setLayout] = useState(() => {
    const stored = localStorage.getItem(`treemapLayout-${pageKey}`);
    return stored === 'vertical' ? 'vertical' : 'horizontal';
  });

  const viewModeStorageKey = pageKey
    ? `treemapSharedViewMode-${pageKey}`
    : 'treemapViewMode';

  // Shared view mode (used for both single and dual modes)
  const [sharedViewMode, setSharedViewMode] = useState(() => {
    const stored = localStorage.getItem(viewModeStorageKey) || localStorage.getItem('treemapViewMode');
    return stored === 'ticker' ? 'ticker' : 'category';
  });

  // Gain basis for single mode
  const [gainBasis, setGainBasis] = useState(() => {
    const stored = localStorage.getItem('treemapGainBasis');
    return stored === 'total' ? 'total' : 'today';
  });

  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist dual mode preference
  useEffect(() => {
    localStorage.setItem(`treemapDualMode-${pageKey}`, isDualMode ? 'true' : 'false');
  }, [isDualMode, pageKey]);

  // Persist layout preference
  useEffect(() => {
    localStorage.setItem(`treemapLayout-${pageKey}`, layout);
  }, [layout, pageKey]);

  // Persist shared view mode
  useEffect(() => {
    localStorage.setItem(viewModeStorageKey, sharedViewMode);
  }, [sharedViewMode, viewModeStorageKey]);

  useEffect(() => {
    if (!pageKey) {
      return;
    }
    const storedDualMode = localStorage.getItem(`treemapDualMode-${pageKey}`);
    setIsDualMode(storedDualMode === 'true');
    const storedLayout = localStorage.getItem(`treemapLayout-${pageKey}`);
    setLayout(storedLayout === 'vertical' ? 'vertical' : 'horizontal');
  }, [pageKey]);

  useEffect(() => {
    const stored = localStorage.getItem(viewModeStorageKey) || localStorage.getItem('treemapViewMode');
    setSharedViewMode(stored === 'ticker' ? 'ticker' : 'category');
  }, [viewModeStorageKey]);

  // Persist gain basis for single mode
  useEffect(() => {
    localStorage.setItem('treemapGainBasis', gainBasis);
  }, [gainBasis]);


  // Determine which layout class to use
  const getLayoutClassName = () => {
    if (!isDualMode) return '';
    // On mobile, always use vertical layout
    if (isMobile) return 'treemap-dual-vertical';
    // On desktop, use user's preference
    return layout === 'vertical' ? 'treemap-dual-vertical' : 'treemap-dual-horizontal';
  };

  // Pre-Bal calculation
  const calculatePreBal = () => {
    if (previousBalance !== null && previousBalance !== undefined) {
      const hasTickers = Array.isArray(tickers) && tickers.length > 0;
      const holdingsCurrent = hasTickers
        ? tickers.reduce((sum, ticker) => sum + (ticker.current_value || 0), 0)
        : 0;
      const cashValue = typeof cash === 'number' && !isNaN(cash) ? cash : 0;
      let currentTotal = holdingsCurrent + cashValue;

      // When there are no tickers, treat missing/zero cash as unchanged balance.
      if (!hasTickers && cashValue <= 0 && previousBalance > 0) {
        currentTotal = previousBalance;
      }
      const gainAmount = currentTotal - previousBalance;
      const gainPct = previousBalance > 0 ? (gainAmount / previousBalance) * 100 : 0;

      if (gainAmount >= 0) {
        return (
          <span className="text-success">
            ${Math.round(gainAmount)} ({gainPct.toFixed(2)}%)
          </span>
        );
      }
      return (
        <span className="text-danger">
          -${Math.abs(Math.round(gainAmount))} (-{Math.abs(gainPct).toFixed(2)}%)
        </span>
      );
    }

    return (
      <span
        className="text-muted"
        title="Previous day balance not available. Balance snapshots are recorded at market close."
      >
        N/A
      </span>
    );
  };

  const preBal = (
    <div className="fw-bold treemap-prebal">
      <span>
        Pre-Bal: {calculatePreBal()}
      </span>
    </div>
  );

  // Color Legend component (shared)
  const colorLegend = (
    <Dropdown align="end">
      <Dropdown.Toggle variant="outline-secondary" size="sm" id="color-legend-dropdown">
        <i className="fas fa-palette me-1"></i> Legend
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: '220px', maxHeight: '600px', overflowY: 'auto' }}>
        <Dropdown.ItemText>
          <div className="d-flex flex-column gap-2">
            <div className="fw-bold text-success mb-1">Gains</div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(144, 238, 144)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">0% to 3%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(60, 179, 113)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">3% to 5%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(0, 128, 0)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">5% to 9%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(0, 100, 0)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body fw-bold">9% and above</span>
            </div>

            <div className="fw-bold text-danger mt-2 mb-1">Losses</div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(255, 182, 193)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">0% to -3%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(220, 20, 60)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">-3% to -5%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(178, 34, 34)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body">-5% to -9%</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: 'rgb(139, 0, 0)',
                border: '1px solid #ddd',
                borderRadius: '3px'
              }}></div>
              <span className="small text-body fw-bold">-9% and below</span>
            </div>
          </div>
        </Dropdown.ItemText>
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <div className="treemap-container">
      {/* Unified Control bar */}
      <div className="treemap-container-controls">
        {/* Title - always show */}
        <h5 className="mb-0">TreeMap</h5>

        {/* Single/Dual Mode Toggle */}
        <ButtonGroup size="sm">
          <Button
            variant={!isDualMode ? 'primary' : 'outline-primary'}
            onClick={() => setIsDualMode(false)}
          >
            Single
          </Button>
          <Button
            variant={isDualMode ? 'primary' : 'outline-primary'}
            onClick={() => setIsDualMode(true)}
          >
            Dual
          </Button>
        </ButtonGroup>

        {/* Gain Basis Selector - show in single mode only */}
        {!isDualMode && (
          <Form.Group controlId="gain-basis-select" className="d-flex align-items-center mb-0">
            <Form.Select
              size="sm"
              value={gainBasis}
              onChange={(e) => setGainBasis(e.target.value)}
            >
              <option value="today">Today's Gain%</option>
              <option value="total">Total Gain%</option>
            </Form.Select>
          </Form.Group>
        )}

        {/* View Mode Selector - always show */}
        <Form.Group controlId="shared-view-select" className="d-flex align-items-center mb-0">
          <Form.Select
            size="sm"
            value={sharedViewMode}
            onChange={(e) => setSharedViewMode(e.target.value)}
          >
            <option value="category">Category</option>
            <option value="ticker">Ticker</option>
          </Form.Select>
        </Form.Group>

        {/* Layout Toggle - only show in dual mode and on desktop */}
        {isDualMode && !isMobile && (
          <ButtonGroup size="sm">
            <Button
              variant={layout === 'horizontal' ? 'primary' : 'outline-primary'}
              onClick={() => setLayout('horizontal')}
              title="Side-by-side layout"
            >
              <i className="bi bi-layout-split"></i> Horizontal
            </Button>
            <Button
              variant={layout === 'vertical' ? 'primary' : 'outline-primary'}
              onClick={() => setLayout('vertical')}
              title="Stacked layout"
            >
              <i className="bi bi-layout-three-columns"></i> Vertical
            </Button>
          </ButtonGroup>
        )}

        {/* Color Legend - always show */}
        {colorLegend}

        {/* Show mobile layout indicator */}
        {isDualMode && isMobile && (
          <span className="text-muted small">
            <i className="bi bi-phone"></i> Mobile: Stacked
          </span>
        )}

        {/* Pre-Bal - always show at the rightmost end */}
        {!preBalLoading && (
          <div className="ms-auto">
            {preBal}
          </div>
        )}
      </div>

      {/* TreeMap(s) */}
      {!isDualMode ? (
        // Single Mode - render one treemap with external controls
        <TreeMapModal
          show={show}
          onHide={onHide}
          tickers={tickers}
          cash={cash}
          previousBalance={previousBalance}
          lastRefreshTime={lastRefreshTime}
          refreshInterval={refreshInterval}
          autoRefreshEnabled={autoRefreshEnabled}
          onRefresh={onRefresh}
          marketInfo={marketInfo}
          inline={inline}
          storageKeyPrefix=""
          hideTitle={true}
          hidePreBal={true}
          hideControls={true}
          hideLegend={true}
          externalViewMode={sharedViewMode}
          onViewModeChange={setSharedViewMode}
          externalGainBasis={gainBasis}
          onGainBasisChange={setGainBasis}
        />
      ) : (
        // Dual Mode - render two treemaps with different prefixes
        // Key prop forces remount when layout changes, ensuring canvas resizes correctly
        <div className={getLayoutClassName()}>
          <TreeMapModal
            key={`left-${layout}`}
            show={show}
            onHide={onHide}
            tickers={tickers}
            cash={cash}
            previousBalance={previousBalance}
            lastRefreshTime={lastRefreshTime}
            refreshInterval={refreshInterval}
            autoRefreshEnabled={autoRefreshEnabled}
            onRefresh={onRefresh}
            marketInfo={marketInfo}
            inline={inline}
            storageKeyPrefix="left-"
            hideControls={true}
            hideLegend={true}
            hideTitle={true}
            hidePreBal={true}
            externalViewMode={sharedViewMode}
            onViewModeChange={setSharedViewMode}
            // Left is always "today" in dual mode (see the isDualMode effect
            // above) -- passed directly rather than left to TreeMapModal's
            // own localStorage-backed internalUseToday, which on a fresh
            // mount with dual mode already remembered as on would read
            // left-/right-treemapGainBasis *before* that effect has run to
            // force them, since a child's useState initializer runs in the
            // same render pass as the parent, before any effects fire.
            externalGainBasis="today"
          />
          <TreeMapModal
            key={`right-${layout}`}
            show={show}
            onHide={onHide}
            tickers={tickers}
            cash={cash}
            previousBalance={previousBalance}
            lastRefreshTime={lastRefreshTime}
            refreshInterval={refreshInterval}
            autoRefreshEnabled={autoRefreshEnabled}
            onRefresh={onRefresh}
            marketInfo={marketInfo}
            inline={inline}
            storageKeyPrefix="right-"
            hideControls={true}
            hideLegend={true}
            hideTitle={true}
            hidePreBal={true}
            externalViewMode={sharedViewMode}
            onViewModeChange={setSharedViewMode}
            // Right is always "total" in dual mode -- see the matching note
            // on the left instance above.
            externalGainBasis="total"
          />
        </div>
      )}
    </div>
  );
}

export default TreeMapContainer;
